import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Flame, Sparkles, Target, Heart,
  Users, Zap, Leaf, Diamond, CheckCircle2, TrendingUp
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';
import FloatingNav from './FloatingNav';
import '../styles/design-system.css';

// ── Utils ────────────────────────────────────────────────────────────────────
const scrollToContact = (navigate) => {
  if (window.location.pathname === '/') {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  } else {
    navigate('/#contact');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }
};

// ── Animation Variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11 } },
};

// ── SVG Icons ────────────────────────────────────────────────────────────────
const EagleLogo = ({ size = 40, color = 'var(--accent-gold-400)' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="50" cy="58" rx="10" ry="14" fill={color} opacity="0.9"/>
    <circle cx="50" cy="38" r="7" fill={color}/>
    <path d="M50 42 L55 46 L50 45 Z" fill={color} opacity="0.7"/>
    <path d="M50 52 C35 44, 12 50, 5 42 C14 38, 32 46, 50 52 Z" fill={color} opacity="0.85"/>
    <path d="M50 52 C65 44, 88 50, 95 42 C86 38, 68 46, 50 52 Z" fill={color} opacity="0.85"/>
    <path d="M5 42 C8 36, 18 40, 28 46" stroke={color} strokeWidth="1" opacity="0.5" fill="none"/>
    <path d="M95 42 C92 36, 82 40, 72 46" stroke={color} strokeWidth="1" opacity="0.5" fill="none"/>
    <path d="M44 70 L40 82 L50 75 L60 82 L56 70 Z" fill={color} opacity="0.7"/>
    <circle cx="53" cy="36" r="1.5" fill="white"/>
  </svg>
);

const FlameIcon = ({ size = 40, color = 'var(--accent-gold-400)' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 10 C50 10, 72 30, 72 52 C72 66, 62 78, 50 82 C38 78, 28 66, 28 52 C28 30, 50 10, 50 10Z" fill={color} opacity="0.9"/>
    <path d="M50 36 C50 36, 62 48, 62 58 C62 66, 56.5 72, 50 74 C43.5 72, 38 66, 38 58 C38 48, 50 36, 50 36Z" fill="white" opacity="0.25"/>
    <ellipse cx="50" cy="64" rx="8" ry="6" fill="white" opacity="0.15"/>
  </svg>
);

// ── Data ──────────────────────────────────────────────────────────────────────
const proposal = [
  { icon: '✝️', label: 'Fé aplicada' },
  { icon: '💰', label: 'Educação Financeira' },
  { icon: '🧠', label: 'Inteligência emocional' },
  { icon: '🔬', label: 'Comportamento humano' },
  { icon: '🎯', label: 'Visão estratégica de negócios' },
];

const forWhom = [
  'Desejam alinhar seus negócios aos princípios do Reino',
  'Buscam clareza de propósito e direção estratégica',
  'Reconhecem que mentalidade e comportamento determinam resultados',
  'Querem crescer sem perder a essência espiritual',
  'Decidiram não se conformar, mas se transformar',
];

const howItWorks = [
  { icon: Users, title: 'Networking com propósito', desc: 'Ambientes de conexões intencionais para expansão e colaboração' },
  { icon: Target, title: 'Direção estratégica', desc: 'Direcionamentos práticos para decisões e crescimento sustentável' },
  { icon: Zap, title: 'Ativações de resultado', desc: 'Destravam mentalidade, posicionamento e resultados concretos' },
  { icon: Heart, title: 'Desenvolvimento emocional', desc: 'Inteligência emocional aplicada ao mundo dos negócios' },
  { icon: Sparkles, title: 'Alinhamento espiritual', desc: 'Para sustentar expansão com sabedoria e propósito' },
];

const differentials = [
  'Espiritualidade prática',
  'Educação Financeira',
  'Psicoterapia cristã',
  'Neurociência e comportamento humano',
  'Inteligência emocional',
  'Estratégia aplicada a negócios',
];

const ctaWords = ['Desperte.', 'Conecte-se.', 'Alinhe-se.', 'Destrave.', 'Prospere.'];

// ── Component ─────────────────────────────────────────────────────────────────
const ProjetoProspere = () => {
  const { theme } = useLanguage();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const cardBg = isDark ? 'rgba(28,28,30,0.75)' : 'rgba(255,255,255,0.9)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
      <Header />

      <main style={{ paddingTop: 80 }}>

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #0a0a0a 0%, #1a0e00 50%, #0f0800 100%)'
              : 'linear-gradient(135deg, #fff8ee 0%, #fef3e0 50%, #fff9f0 100%)',
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Decorative glows */}
          <div style={{
            position: 'absolute', top: '-15%', right: '-8%',
            width: 600, height: 600,
            background: 'radial-gradient(circle, rgba(184,134,11,0.15) 0%, transparent 70%)',
            filter: 'blur(80px)', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: '-10%', left: '-8%',
            width: 500, height: 500,
            background: 'radial-gradient(circle, rgba(220,100,0,0.08) 0%, transparent 70%)',
            filter: 'blur(70px)', pointerEvents: 'none',
          }} />

          {/* Floating flame motif */}
          <div style={{
            position: 'absolute', right: '8%', top: '50%', transform: 'translateY(-50%)',
            opacity: 0.06, pointerEvents: 'none',
          }}>
            <FlameIcon size={320} color="var(--accent-gold-400)" />
          </div>

          <div className="container relative z-10 py-24">
            {/* Back link */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12 flex items-center gap-4 flex-wrap"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm"
                style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
              >
                <ArrowLeft size={16} />
                Início
              </Link>
              <span style={{ color: 'var(--text-muted)', opacity: 0.4 }}>/</span>
              <Link
                to="/projeto-aguias"
                className="inline-flex items-center gap-2 text-sm"
                style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
              >
                <EagleLogo size={14} />
                Projeto Águias
              </Link>
              <span style={{ color: 'var(--text-muted)', opacity: 0.4 }}>/</span>
              <span className="text-sm" style={{ color: 'var(--accent-gold-400)' }}>Prospere</span>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="max-w-3xl"
            >
              {/* Parent badge */}
              <motion.div variants={fadeUp} className="mb-4">
                <Link
                  to="/projeto-aguias"
                  style={{ textDecoration: 'none' }}
                >
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full"
                    style={{
                      background: 'rgba(184,134,11,0.06)',
                      border: '1px solid rgba(184,134,11,0.2)',
                    }}
                  >
                    <EagleLogo size={14} />
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem', letterSpacing: '0.1em', fontFamily: 'monospace' }}>
                      UMA EXTENSÃO DO PROJETO ÁGUIAS
                    </span>
                  </div>
                </Link>
              </motion.div>

              {/* Main badge */}
              <motion.div variants={fadeUp} className="mb-6">
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
                  style={{
                    background: 'rgba(184,134,11,0.1)',
                    border: '1px solid rgba(184,134,11,0.35)',
                  }}
                >
                  <FlameIcon size={20} />
                  <span
                    className="mono-text"
                    style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.14em' }}
                  >
                    PROSPERE — NEGÓCIOS COM DEUS
                  </span>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1 variants={fadeUp} className="heading-hero mb-5">
                Conexões que{' '}
                <span className="gold-accent">destravam</span>
                <br />
                resultados.
              </motion.h1>

              <motion.p variants={fadeUp} className="body-large mb-4" style={{ maxWidth: 600 }}>
                Um movimento do Projeto Águias para empreendedores, empresários e líderes
                que compreenderam que resultado sem propósito é vazio —
                e propósito sem ação não prospera.
              </motion.p>

              <motion.p variants={fadeUp} className="body-medium mb-10" style={{ maxWidth: 560, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                O conformismo limita. A transformação posiciona.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToContact(navigate)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Quero participar
                  <ArrowRight size={16} />
                </button>
                <Link to="/projeto-aguias" className="btn-secondary inline-flex items-center gap-2">
                  <EagleLogo size={16} />
                  Ver Projeto Águias
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Nossa Proposta ────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-page)' : '#FFFFFF' }}>
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16 items-center">

              {/* Left: text */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.div variants={fadeUp} className="mb-8">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                    style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                  >
                    <Sparkles size={14} style={{ color: 'var(--accent-gold-400)' }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                      NOSSA PROPOSTA
                    </span>
                  </div>
                  <h2 className="heading-1 mb-4">
                    Desenvolver líderes<br />de forma integral.
                  </h2>
                  <p className="body-medium" style={{ maxWidth: 480 }}>
                    Promovemos conexões estratégicas e direcionamento prático para
                    gerar resultados consistentes e sustentáveis —
                    porque prosperidade verdadeira envolve <strong>espírito, alma e estrutura.</strong>
                  </p>
                </motion.div>

                {/* Quote box */}
                <motion.div
                  variants={fadeUp}
                  className="relative p-8 rounded-2xl"
                  style={{
                    background: isDark ? '#1a1a1a' : 'var(--bg-page)',
                    border: '1px solid var(--accent-gold-400)',
                  }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, padding: '1rem', opacity: 0.07 }}>
                    <TrendingUp size={80} style={{ color: 'var(--accent-gold-400)' }} />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div style={{ width: 4, height: 24, background: 'var(--accent-gold-400)', borderRadius: 99 }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.7rem', letterSpacing: '0.12em' }}>
                      NOSSA MISSÃO
                    </span>
                  </div>
                  <p className="font-serif italic text-xl leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                    "Despertar, destravar e posicionar líderes para prosperarem com consciência, equilíbrio e direção."
                  </p>
                </motion.div>
              </motion.div>

              {/* Right: pillars */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.p variants={fadeUp} className="mono-text mb-6" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  AQUI, UNIMOS
                </motion.p>
                <div className="space-y-3">
                  {proposal.map(({ icon, label }, i) => (
                    <motion.div
                      key={label}
                      variants={fadeUp}
                      whileHover={{ x: 6 }}
                      className="flex items-center gap-4 p-5 rounded-2xl"
                      style={{
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        backdropFilter: 'blur(12px)',
                      }}
                    >
                      <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{icon}</span>
                      <span className="body-medium" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{label}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── Para Quem ─────────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-section)' : '#FAFAFA' }}>
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
              >
                <Heart size={14} style={{ color: 'var(--accent-gold-400)' }} />
                <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  PARA QUEM É
                </span>
              </div>
              <h2 className="heading-1 mb-4">
                Para quem decidiu sair<br />do <span className="gold-accent">nível comum.</span>
              </h2>
              <p className="body-medium mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: 520 }}>
                O Prospere é para empreendedores, empresários e líderes que acessaram
                uma nova consciência de prosperidade.
              </p>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto"
            >
              {forWhom.map((text, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(184,134,11,0.12)' }}
                  className="flex items-start gap-3 p-5 rounded-xl"
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    backdropFilter: 'blur(12px)',
                    transition: 'box-shadow 0.3s',
                  }}
                >
                  <CheckCircle2
                    size={18}
                    style={{ color: 'var(--accent-gold-400)', flexShrink: 0, marginTop: 2 }}
                  />
                  <span className="body-small" style={{ color: 'var(--text-primary)', fontSize: '0.9rem', lineHeight: 1.55 }}>
                    {text}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Como Funciona ─────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-page)' : '#FFFFFF' }}>
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
              >
                <Zap size={14} style={{ color: 'var(--accent-gold-400)' }} />
                <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  COMO FUNCIONA
                </span>
              </div>
              <h2 className="heading-1">Encontros presenciais e<br />conexões intencionais</h2>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto"
            >
              {howItWorks.map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  className="p-6 rounded-2xl flex flex-col gap-3"
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(184,134,11,0.1)' }}
                  >
                    <Icon size={20} style={{ color: 'var(--accent-gold-400)' }} />
                  </div>
                  <h3 className="heading-3">{title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{desc}</p>
                </motion.div>
              ))}

              {/* Spanning card */}
              <motion.div
                variants={fadeUp}
                whileHover={{ y: -6 }}
                className="p-6 rounded-2xl flex flex-col gap-3 md:col-span-2 lg:col-span-1"
                style={{
                  background: 'rgba(184,134,11,0.06)',
                  border: '1px solid rgba(184,134,11,0.3)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FlameIcon size={28} />
                  <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.7rem', letterSpacing: '0.1em' }}>
                    RESULTADO REAL
                  </span>
                </div>
                <p className="body-medium" style={{ color: 'var(--text-primary)', fontStyle: 'italic', lineHeight: 1.65 }}>
                  "Mais do que crescimento, promovemos destraves que geram resultados com firmeza, constância e abundância."
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Nosso Diferencial ─────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-section)' : '#FAFAFA' }}>
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16 items-start">

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
              >
                <motion.div variants={fadeUp}>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                    style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                  >
                    <Diamond size={14} style={{ color: 'var(--accent-gold-400)' }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                      NOSSO DIFERENCIAL
                    </span>
                  </div>
                  <h2 className="heading-1 mb-4">Conduzido por<br />Lucimar Bizoni</h2>
                  <p className="body-medium mb-8" style={{ maxWidth: 440 }}>
                    Uma abordagem integrativa única que não separa o interior do exterior —
                    porque não existe crescimento externo sustentável sem alinhamento interno.
                    <br /><br />
                    <em>É a transformação que sustenta o resultado.</em>
                  </p>
                </motion.div>
                <div className="space-y-3">
                  {differentials.map((item, i) => (
                    <motion.div
                      key={i}
                      variants={fadeUp}
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-gold-400)', flexShrink: 0 }} />
                      <span className="body-small" style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Posicionamento block */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-3xl p-10 relative overflow-hidden"
                style={{
                  background: isDark
                    ? 'linear-gradient(135deg, #1a1000 0%, #0f0800 100%)'
                    : 'linear-gradient(135deg, #fef3d0 0%, #fff8e8 100%)',
                  border: '1px solid rgba(184,134,11,0.3)',
                }}
              >
                <div style={{ position: 'absolute', top: -20, right: -20, opacity: 0.06 }}>
                  <FlameIcon size={200} color="var(--accent-gold-400)" />
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <div style={{ width: 4, height: 28, background: 'var(--accent-gold-400)', borderRadius: 99 }} />
                  <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.7rem', letterSpacing: '0.12em' }}>
                    POSICIONAMENTO
                  </span>
                </div>

                <p className="font-serif italic leading-relaxed mb-6" style={{ fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                  "O conformismo limita. A transformação posiciona."
                </p>
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Quando você decide se transformar, seu potencial deixa de ser
                  promessa e se torna <strong style={{ color: 'var(--accent-gold-400)' }}>potência no Reino de Deus.</strong>
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Aqui, destravamos vidas para resultados com
                  <strong> firmeza, constância e abundância.</strong>
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── CTA Final ─────────────────────────────────────────────────────── */}
        <section
          className="py-28 relative overflow-hidden"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #0a0500 0%, #1a0e00 50%, #0a0800 100%)'
              : 'linear-gradient(135deg, #fdf6e3 0%, #fef0c7 50%, #fdf6e3 100%)',
          }}
        >
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700, height: 700,
            background: 'radial-gradient(circle, rgba(184,134,11,0.14) 0%, transparent 70%)',
            filter: 'blur(80px)', pointerEvents: 'none',
          }} />

          <div className="container relative z-10 text-center">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="max-w-2xl mx-auto"
            >
              <motion.div variants={fadeUp} style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <FlameIcon size={72} />
              </motion.div>

              <motion.h2 variants={fadeUp} className="heading-hero mb-6">
                Se você foi chamado<br />para mais —{' '}
                <span className="gold-accent">esse é seu ambiente.</span>
              </motion.h2>

              <motion.div
                variants={fadeUp}
                className="flex flex-wrap justify-center gap-2 mb-10"
              >
                {ctaWords.map((w, i) => (
                  <motion.span
                    key={w}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.12 }}
                    style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontStyle: 'italic',
                      fontSize: '1.35rem',
                      color: i === 4 ? 'var(--accent-gold-400)' : 'var(--text-secondary)',
                      fontWeight: i === 4 ? 700 : 400,
                    }}
                  >
                    {w}
                  </motion.span>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => scrollToContact(navigate)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Quero me transformar
                  <ArrowRight size={16} />
                </button>
                <Link to="/projeto-aguias" className="btn-secondary inline-flex items-center gap-2">
                  <EagleLogo size={16} />
                  Projeto Águias
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </main>

      <Footer />
      <FloatingWhatsApp />
      <FloatingNav />
    </div>
  );
};

export default ProjetoProspere;
